import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import type { NewsCluster } from '../../types/types';

const TYPE_PALETTE = [
    { bg: '#1e3a8a', border: '#60a5fa', text: '#bfdbfe' },
    { bg: '#581c87', border: '#c084fc', text: '#e9d5ff' },
    { bg: '#14532d', border: '#4ade80', text: '#bbf7d0' },
] as const;

export function getClusterKeywordParts(cluster: NewsCluster): string[] {
    const raw = cluster.keywords;
    const parts = Array.isArray(raw)
        ? raw.map((k) => String(k).trim()).filter(Boolean)
        : String(raw ?? '')
              .split(',')
              .map((k) => k.trim())
              .filter(Boolean);
    return parts;
}

/** Up to `max` keywords, single line with ellipsis. */
export function RadarKeywordsRow({ cluster, max = 3 }: { cluster: NewsCluster; max?: number }) {
    const parts = getClusterKeywordParts(cluster).slice(0, max);
    return (
        <Typography
            component="div"
            variant="body2"
            sx={{
                color: '#d4d4d8',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
            }}
        >
            {parts.map((keyword, idx) => (
                <React.Fragment key={idx}>
                    {idx > 0 && (
                        <Box component="span" sx={{ color: '#a1a1aa', mx: 0.5 }}>
                            •
                        </Box>
                    )}
                    <Box component="span" sx={{ fontWeight: 'bold', color: '#fafafa' }}>
                        {keyword}
                    </Box>
                </React.Fragment>
            ))}
        </Typography>
    );
}

/** Squared chips: `compact` = single row with ellipsis; `wrap` = multi-line friendly for wide cells. */
export function RadarArticleTypeChipsRow({
    types,
    max = 3,
    layout = 'compact',
}: {
    types: string[];
    max?: number;
    layout?: 'compact' | 'wrap';
}) {
    const wrap = layout === 'wrap';
    const display = types.slice(0, max);
    const extra = types.length > max ? types.length - max : 0;
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: wrap ? 'wrap' : 'nowrap',
                gap: 0.5,
                alignItems: wrap ? 'flex-start' : 'center',
                minWidth: 0,
                overflow: wrap ? 'visible' : 'hidden',
            }}
        >
            {display.map((type, idx) => {
                const c = TYPE_PALETTE[idx % TYPE_PALETTE.length];
                return (
                    <Tooltip key={`${idx}-${type}`} title={type} arrow placement="top">
                        <Chip
                            label={type}
                            size="small"
                            sx={{
                                height: wrap ? 'auto' : 22,
                                minHeight: 22,
                                flex: wrap ? '0 1 auto' : '1 1 0',
                                minWidth: 0,
                                maxWidth: wrap ? '100%' : '100%',
                                borderRadius: 0,
                                '& .MuiChip-label': {
                                    px: 0.5,
                                    fontSize: '0.7rem',
                                    fontWeight: 700,
                                    overflow: wrap ? 'visible' : 'hidden',
                                    textOverflow: wrap ? 'clip' : 'ellipsis',
                                    whiteSpace: wrap ? 'normal' : 'nowrap',
                                    display: 'block',
                                    lineHeight: 1.35,
                                    py: wrap ? 0.25 : 0,
                                },
                                bgcolor: c.bg,
                                color: c.text,
                                border: `1px solid ${c.border}`,
                            }}
                        />
                    </Tooltip>
                );
            })}
            {extra > 0 && (
                <Tooltip title={types.slice(max).join(', ')} arrow placement="top">
                    <Chip
                        label={`+${extra}`}
                        size="small"
                        sx={{
                            height: 22,
                            flexShrink: 0,
                            borderRadius: 0,
                            bgcolor: '#27272a',
                            color: '#a1a1aa',
                            border: '1px solid #52525b',
                            '& .MuiChip-label': { px: 0.5, fontSize: '0.7rem', fontWeight: 700 },
                        }}
                    />
                </Tooltip>
            )}
        </Box>
    );
}
